<template>
    <span data-component="SessionSpaceCounts"
          class="session-space-counts">
        <!-- The whitespace (or lack thereof) in these spans is important to avoid extra spaces being rendered. -->
        <template v-for="(countBadge, idx) in spaceCountBadges"
                  :key="`space-count-badge-${countBadge}-${idx}`">
            <span v-if="!!countBadge.isShow"
                  class="label-default session-space-count"
                  :class="countBadge.specialClass">
                {{ countBadge.count }} {{ countBadge.label }}
            </span>
        </template>
    </span>
</template>

<script>
/**
 * @typedef {import('@/lib/Sessions/SessionAvailability')} SessionAvailability
 */

/**
 * @typedef {{
 *     isShow: boolean,
 *     count: number|null,
 *     label: string,
 *     specialClass: string,
 * }} SpaceCountBadge
 */

export default {
    name: 'SessionSpaceCounts',

    props: {
        // Always show a minimum of Sold + Available badges.
        linearMode: {
            type: Boolean,
            default: false,
        },
        availability: {
            type: Object,
            required: true,
        },

        spacesRequired: {
            type: Number,
            default: null,
        },
    },

    computed: {
        spacesBookable() {
            return this.availability.getSpacesBookable();
        },

        isBookable() {
            if (this.spacesRequired !== null) {
                return this.spacesBookable >= this.spacesRequired;
            }

            return this.spacesBookable > 0;
        },

        /** @return {SpaceCountBadge[]} */
        spaceCountBadges() {
            /**
             * @type {SessionAvailability}
             */
            const { spacesInCart, spacesSold, spacesReserved, groupBookingCount, spacesBookable } = this.availability;
            return [
                {
                    isShow: spacesInCart !== null,
                    count: spacesInCart,
                    label: this.l('SESSION_PICKER.SPACES_IN_CART'),
                    specialClass: 'session-space-count--in-cart',
                },
                // Show sold/reserved badges instead of sum of both.
                {
                    isShow: spacesSold > 0 || this.linearMode,
                    count: spacesSold,
                    label: this.l('SESSION_PICKER.SPACES_SOLD'),
                    specialClass: 'session-space-count--sold',
                },
                {
                    isShow: spacesReserved > 0 || this.linearMode,
                    count: spacesReserved,
                    label: this.l('SESSION_PICKER.SPACES_RESERVED'),
                    specialClass: 'session-space-count--reserved',
                },
                {
                    isShow: groupBookingCount > 0,
                    count: groupBookingCount,
                    label: this.l('SESSION_PICKER.GROUP_BOOKING_COUNT', { qty: groupBookingCount }),
                    specialClass: 'session-space-count--groups',
                },
                {
                    isShow: true,
                    count: spacesBookable,
                    label: this.l('SESSION_PICKER.SPACES_AVAILABLE'),
                    specialClass: this.isBookable ? 'session-space-count--available' : 'session-space-count--unavailable',
                },
            ];
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../less/variables";

.session-space-count {
    font-size: 12px;
    font-weight: normal;
    padding: 2px 6px;
    color: @white;
    border-radius: @border-radius-base;

    &--sold {
        background-color: @dt-navy;
        border-color: @dt-navy;
    }

    &--reserved {
        background-color: @dt-dark-grey;
        border-color: @dt-dark-grey;
    }

    &--available {
        background-color: @brand-success;
        border-color: @brand-success;
    }

    &--in-cart {
        background-color: @brand-info;
        border-color: @brand-warning;
    }

    &--unavailable {
        background-color: @brand-danger;
        border-color: @brand-danger;
    }

    &--groups {
        background-color: @purple;
        border-color: @purple;
    }

    &:not(:first-child) {
        margin-left: 3px;
    }
}
</style>
